import { type SignInStatus, signOutRequest } from '@ionq/auth-api';

// The SSO sign-out leg, adapted from ionq-cloud-ui (src/auth/session.ts):
// rebuilt shell-side because the remote exposes no session module. Retires
// with console-auth. Every handoff targets this origin's own /auth/* mount:
// `redirectTo` (validated server-side by the SSO) replaces the legacy
// client_id/redirect_uri/target_uri trio, and sign-out falls through to
// sign-in with the query intact.

export const getSignOutURL = ({
    email,
    targetUri,
}: {
    email?: string;
    targetUri?: string;
} = {}) => {
    const url = new URL('/auth/sign-out', window.location.origin);
    if (targetUri) url.searchParams.set('redirectTo', targetUri);
    if (email) url.searchParams.set('email', email);
    return url.toString();
};

export const getSignInURL = ({
    email,
    targetUri,
    status,
}: {
    email?: string;
    targetUri?: string;
    status?: SignInStatus;
} = {}) => {
    const url = new URL('/auth/sign-in', window.location.origin);
    if (targetUri) url.searchParams.set('redirectTo', targetUri);
    if (email) url.searchParams.set('email', email);
    if (status) url.searchParams.set('status', status);
    return url.toString();
};

/** Best-effort cookie revoke + the sign-in URL to land on. The loader-shaped
 * variant of signOut: router redirects need a URL, not an imperative
 * navigation. */
export const signOutRedirect = async (
    params: { email?: string; targetUri?: string; status?: SignInStatus } = {},
) => {
    await signOutRequest();
    return getSignInURL(params);
};

/** Direct sign-out: POST the same-origin /auth/api/sign-out — the cookies
 * expire on that response — then land on the mounted sign-in with the same
 * query the SSO SignOut page used to forward. Replace keeps the authed page
 * out of history. */
export const signOut = async (
    params: { email?: string; targetUri?: string } = {},
) => {
    window.location.replace(await signOutRedirect(params));
};

// The in-app landing after an account switch: the same location, app-
// relative. Org URLs are id-less (cloud-ui dropped the /organization/:id
// segment), so the current URL stays valid in the new org — no rewrite.
export const getSwitchLandingURL = ({ from }: { from: string }) => {
    const url = new URL(from, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
};
